import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ActionPreview, AnyScreenAction, ScreenAction, ScreenManifest } from '@/shared/screen/types';

import type { FilterField, FilterSet } from '@/shared/filters/define-filters';

import { legalPersonFilters, naturalPersonFilters } from './person.filters';
import { PERSON_KINDS, personListPath, personPendingPath, personRecordPath, type PersonKind } from './person.kind';

// Manifestos das quatro listas (U23/U42): "Todas" e "Pendentes" de PF e PJ, o mesmo contêiner do legado
// com `isPendingPreApprove` (inventário §2). A exclusão tem prévia com o nome (U24) e roda pelo fio com
// import dinâmico, para o manifesto continuar leve.

const api = () => import('./person.api');
const idInput = z.object({ id: z.string().min(1), name: z.string().optional() });

export interface PersonListActions {
  readonly create: ScreenAction<Record<string, never>>;
  readonly open: ScreenAction<z.infer<typeof idInput>>;
  readonly remove: ScreenAction<z.infer<typeof idInput>>;
  readonly filter: AnyScreenAction;
  readonly page: ReturnType<typeof listPageAction>;
}

const TEXT: Readonly<Record<PersonKind, { readonly confirm: MessageDescriptor }>> = {
  natural: { confirm: msg`Tem certeza que deseja excluir este usuário?` },
  legal: { confirm: msg`Tem certeza que deseja excluir este cadastro?` },
};

/** O que o agente lê de cada ação (U46, D1): pt-BR com `msg`, sem endpoint. */
function agentText(kind: PersonKind, keys: string) {
  return kind === 'natural'
    ? {
        create: msg`Abre o formulário em branco para cadastrar uma pessoa física.`,
        open: msg`Abre o cadastro de uma pessoa física pelo id, com as abas de informações, dados bancários, documentos e as demais.`,
        remove: msg`Exclui o cadastro de uma pessoa física pelo id. Pede a confirmação da pessoa antes.`,
        filter: msg`Filtra a lista de pessoas físicas: todos os campos, correspondente, CPF, período de criação, e-mail, nome e telefone. Filtro omitido é limpo; datas são civis (AAAA-MM-DD). Chaves: ${keys}.`,
      }
    : {
        create: msg`Abre o formulário em branco para cadastrar uma pessoa jurídica.`,
        open: msg`Abre o cadastro de uma pessoa jurídica pelo id, com as abas de informações, dados bancários, documentos, signatários e as demais.`,
        remove: msg`Exclui o cadastro de uma pessoa jurídica pelo id. Pede a confirmação da pessoa antes.`,
        filter: msg`Filtra a lista de pessoas jurídicas: todos os campos, CNPJ, período de criação, e-mail, telefone e razão social. Filtro omitido é limpo; datas são civis (AAAA-MM-DD). Chaves: ${keys}.`,
      };
}

/** Prévia da exclusão: o título do legado é `Excluir #<nome>?` (NaturalPersonListContainer.tsx:149). */
export function personDeletePreview(kind: PersonKind, name: string): ActionPreview {
  return { title: i18n._(msg`Excluir #${name}?`), detail: i18n._(TEXT[kind].confirm), tone: 'destructive' };
}

function listActions<Fs extends readonly FilterField[]>(kind: PersonKind, filters: FilterSet<Fs>): PersonListActions {
  const { resource, newId } = PERSON_KINDS[kind];
  const prefix = `person.${kind === 'natural' ? 'naturalPerson' : 'legalPerson'}`;
  const text = agentText(kind, filters.keys.join(', '));
  return {
    create: action({
      id: `${prefix}.create`,
      kind: 'draft',
      label: msg`Novo cadastro`,
      description: text.create,
      permission: can(resource, 'Create'),
      run: ({ nav }) => nav.to(personRecordPath(kind, newId)),
    }),
    open: action({
      id: `${prefix}.open`,
      kind: 'navigate',
      page: true,
      row: true,
      label: msg`Editar`,
      description: text.open,
      permission: can(resource, 'Read'),
      input: idInput,
      run: ({ nav, input }) => nav.to(personRecordPath(kind, input.id)),
    }),
    remove: action({
      id: `${prefix}.delete`,
      kind: 'destructive',
      page: true,
      row: true,
      label: msg`Excluir`,
      description: text.remove,
      permission: can(resource, 'Delete'),
      input: idInput,
      // Na tela, o `name` vem da própria linha; a ferramenta do agente troca a prévia pela leitura do cache (`person.agent.ts`).
      preview: (input) => personDeletePreview(kind, input.name ?? input.id),
      run: async ({ input }) => {
        const wire = await api();
        await (kind === 'natural' ? wire.deleteNaturalPerson(input.id) : wire.deleteLegalPerson(input.id));
      },
    }),
    filter: filterAction(`${prefix}.filter`, filters, { description: text.filter }),
    page: listPageAction(`${prefix}.list.page`),
  };
}

export const NATURAL_PERSON_ACTIONS = listActions('natural', naturalPersonFilters);
export const LEGAL_PERSON_ACTIONS = listActions('legal', legalPersonFilters);
export const personListActions = (kind: PersonKind) => (kind === 'natural' ? NATURAL_PERSON_ACTIONS : LEGAL_PERSON_ACTIONS);

function listScreen(kind: PersonKind, pending: boolean): ScreenManifest {
  const config = PERSON_KINDS[kind];
  const actions = personListActions(kind);
  const natural = kind === 'natural';
  return defineScreen({
    id: `person.${natural ? 'naturalPerson' : 'legalPerson'}${pending ? 'Pending' : 'List'}`,
    path: pending ? personPendingPath(kind) : personListPath(kind),
    title: pending ? msg`Pendentes` : natural ? msg`Pessoas Físicas` : msg`Pessoas Jurídicas`,
    description: natural
      ? pending
        ? msg`Pessoas físicas pendentes de pré-aprovação do compliance, com as mesmas colunas e filtros da lista completa; abre e exclui cadastros.`
        : msg`Lista paginada de pessoas físicas (CPF) com os filtros do legado; abre, cadastra e exclui pessoas.`
      : pending
        ? msg`Pessoas jurídicas pendentes de pré-aprovação do compliance, com as mesmas colunas e filtros da lista completa; abre e exclui cadastros.`
        : msg`Lista paginada de pessoas jurídicas (CNPJ) com os filtros do legado; abre, cadastra e exclui empresas.`,
    permission: pending ? can(config.resource, 'ComplianceApproval') : can(config.resource, 'Read'),
    access: { flag: { key: config.menuFlag, notEquals: 0 } },
    // Ícones que a casca já tem no mapa (`src/app/shell/icons.tsx`, U42).
    place: { icon: pending ? 'shield-check' : natural ? 'user' : 'building-2', section: config.section },
    // Pendentes não oferece Novo cadastro (NaturalPersonListHeader.tsx:46).
    actions: pending ? [actions.open, actions.remove, actions.filter, actions.page] : [actions.create, actions.open, actions.remove, actions.filter, actions.page],
  });
}

export const naturalPersonListScreen = listScreen('natural', false);
export const naturalPersonPendingScreen = listScreen('natural', true);
export const legalPersonListScreen = listScreen('legal', false);
export const legalPersonPendingScreen = listScreen('legal', true);
